<template>
  <div class="bi-display-value">
    <dt v-if="title">
      {{ title }}
    </dt>
    <div class="__main-row">
      <dd>
        <BaseLoadingValue :state-loading="value === undefined">
          <template slot="content">
            <span class="__value" :class="{ 'number-monospace': true, [size]: !!size }">{{ displayValue }}</span>
          </template>
        </BaseLoadingValue>
      </dd>
      <div v-if="comparisonValue !== undefined" class="__details">
        <v-popover placement="top" trigger="hover" popoverClass="bi-display-value__tooltip">
          <BaseLoadingValue :state-loading="comparisonValue === undefined">
            <template slot="content">
              <BaseTag
                preset="ghost"
                :icon="isPositive ? 'up' : 'down'"
                :tone="getBiDisplayComparisonTone(value, comparisonValue)"
                class="number-monospace"
              >
                {{ displayComparisonValue }}
              </BaseTag>
            </template>
          </BaseLoadingValue>
          <template v-slot:popover>
            <slot name="tooltip">
              <div>
                {{
                  $tc(
                    'page.business-intelligence.summary.previous-period',
                    !(showComparisonValueInTooltip && comparisonValue !== undefined),
                    {
                      value: display(comparisonValue),
                      dates: displayPreviousPeriod,
                    },
                  )
                }}
              </div>
            </slot>
          </template>
        </v-popover>
      </div>
    </div>
  </div>
</template>
<script>
import BaseLoadingValue from '@/components/base/BaseLoadingValue.vue';
import BaseTag from '@/components/base/BaseTag.vue';

import { getBiDisplayComparisonTone } from '@/util/tag-tones/bi-display-value-tag';
import { calculatePercentage, formatPercentageShort } from '@/util/number-util';
import { formatCurrencyShort } from '@/util/currency-util';
import { formatStandardDate } from '@/util/date-util';

export default {
  name: 'BIDisplayValue',
  components: {
    BaseLoadingValue,
    BaseTag,
  },
  props: {
    title: {
      type: String,
    },
    value: {
      type: [String, Number],
      required: false,
    },
    type: {
      type: String,
      required: false,
      default: 'text', // currency | percentage | number
    },
    size: {
      type: String,
      required: false,
      default: 'small', // small | medium | large
    },
    showComparisonValue: {
      type: Boolean,
      required: false,
      default: false,
    },
    comparisonValue: {
      type: [String, Number],
      required: false,
    },
    showComparisonValueInTooltip: {
      // show comparison value in tooltip
      type: Boolean,
      required: false,
      default: false,
    },
    previousPeriodFrom: {
      type: String,
      required: false,
    },
    previousPeriodTo: {
      type: String,
      required: false,
    },
  },
  computed: {
    diff() {
      return this.value - this.comparisonValue;
    },
    isPositive() {
      return this.diff > 0;
    },
    displayValue() {
      return this.display(this.value);
    },
    displayComparisonValue() {
      let s;
      if (this.showComparisonValue) {
        s = `${this.display(this.diff)}
        (${Math.abs(calculatePercentage(this.diff / (this.comparisonValue || 1))).toFixed(0)}%)`;
      } else {
        s = `${calculatePercentage((this.value - this.comparisonValue) / (this.comparisonValue || 1)).toFixed(0)}%`;
      }
      if (this.isPositive) {
        const t = s.trimStart();
        if (t.startsWith('+')) {
          return s;
        }
        return `+${s}`;
      }
      return s;
    },
    displayPreviousPeriod() {
      if (this.previousPeriodFrom && this.previousPeriodTo) {
        return `${formatStandardDate(this.previousPeriodFrom)} - ${formatStandardDate(this.previousPeriodTo)}`;
      }
      return '';
    },
  },
  methods: {
    getBiDisplayComparisonTone,
    calculatePercentage,
    formatPercentageShort,
    display(value) {
      if (value === null) {
        return '-';
      }

      switch (this.type) {
        case 'currency':
          return formatCurrencyShort(value);
        case 'percentage':
          return formatPercentageShort(value);
        default:
          return value;
      }
    },
  },
};
</script>
<style lang="scss">
.bi-display-value {
  display: flex;
  flex-direction: column;
  min-width: 0;

  .base-icon {
    width: 14px;
    height: 14px;
    color: $color-normal-pale;
    cursor: pointer;
  }

  dt {
    font-weight: 600;
    font-size: $font-size-small;
    color: $color-normal-pale;
  }

  .__main-row {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    gap: 4px;
    min-width: 0;
  }

  dd {
    margin: 0;
    flex-shrink: 0;
    font-weight: 600;
    font-size: $font-size-xbigger;
    color: $color-normal;
  }

  .__details {
    display: flex;
    align-items: center;
    flex-shrink: 0;
  }
  .__value {
    &.small {
      font-size: $font-size-small;
    }
    &.medium {
      font-size: $font-size-xbigger;
    }
    &.large {
      font-size: $font-size-xxbigger;
    }
  }

  // .bi-display-value__comparison-tag {
  //   margin-right: 4px;
  //   font-weight: bold;

  //   &.small {
  //     font-size: $font-size-xsmall;
  //   }
  //   &.medium {
  //     font-size: $font-size-small;
  //   }
  //   &.large {
  //     font-size: $font-size-bigger;
  //   }
  // }

  // .bi-display-value__comparison-tag--positive .base-tag__label::before {
  //   content: '+';
  //   display: inline-block;
  // }
}

.bi-display-value__tooltip {
  padding: 8px;
  font-size: $font-size-xsmall;
  background-color: $color-normal !important;
  color: $color-white !important;
  box-shadow: $shadow-tooltip;

  .popover-arrow {
    background-color: $color-normal !important;
  }
}
</style>
